import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileText, FolderPlus, Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import SettlementAccountSelect from "@/components/aus/SettlementAccountSelect";

const money = (c, v) => `${c}${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Timeline quick action — Statement → view (detailed / summary) → Download PDF / Save to files / Email (wizard). */
export function StatementDialog({ caseRow, onClose, onDone }) {
  const caseId = caseRow?.case_id;
  const [view, setView] = useState("detailed");
  const [scope, setScope] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [emailMode, setEmailMode] = useState(false);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [settlementChoice, setSettlementChoice] = useState(null);
  const req = { view, scope, date_from: dateFrom || null, date_to: dateTo || null, settlement_choice: settlementChoice };

  useEffect(() => {
    if (!caseId) return;
    setLoading(true);
    api.post(`/statements/${caseId}/preview`, req)
      .then((r) => { setPreview(r.data); if (!to) setTo(r.data.email || ""); if (!settlementChoice && r.data.settlement?.options) setSettlementChoice(r.data.settlement.kind); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not build the statement"))
      .finally(() => setLoading(false));
  }, [caseId, view, scope, dateFrom, dateTo, settlementChoice]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async () => {
    setBusy("pdf");
    try {
      const r = await api.post(`/statements/${caseId}/pdf`, req, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      toast.success("Statement PDF ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "PDF failed"); } finally { setBusy(""); }
  };
  const save = async () => {
    setBusy("save");
    try {
      const r = await api.post(`/statements/${caseId}/save`, req);
      toast.success(`Saved to files: ${r.data.file}`); (r.data.warnings || []).forEach((w) => toast.warning(w));
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(""); }
  };
  const send = async () => {
    if (!to.includes("@")) { toast.error("Enter the customer's email"); return; }
    setBusy("send");
    try {
      const r = await api.post(`/statements/${caseId}/send`, { ...req, recipient_email: to, subject: subject || null, body_text: body });
      toast.success(`Statement emailed to ${to}`); (r.data.warnings || []).forEach((w) => toast.warning(w));
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); } finally { setBusy(""); }
  };

  const cur = preview?.currency || "£";
  const chosenOpt = preview?.settlement?.options?.find((o) => o.key === (settlementChoice || preview.settlement.kind));
  const blocked = !preview || !!busy || (!!chosenOpt && !chosenOpt.available);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="statement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-emerald-700" />Client statement — {caseRow?.customer_name}</DialogTitle>
          <DialogDescription>Rebuilt live from LoanDisk (loan terms, adjusted schedule, repayments). Restructured loans are cross-referenced so balances aren't double counted.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Statement</Label>
            <Select value={view} onValueChange={setView}>
              <SelectTrigger className="h-9" data-testid="statement-view-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="detailed">Detailed (per loan)</SelectItem><SelectItem value="summary">Consolidated (summary)</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Loans</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="h-9" data-testid="statement-scope-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All loans of borrower</SelectItem><SelectItem value="current">This loan only</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">From</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9" data-testid="statement-from" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9" data-testid="statement-to" /></div>
        </div>
        {preview?.settlement?.options && (
          <div data-testid="statement-settlement">
            <Label className="text-xs">Payment details printed on the statement</Label>
            <SettlementAccountSelect options={preview.settlement.options} value={settlementChoice || preview.settlement.kind} onChange={setSettlementChoice}
              createHref={`/aus/collections/ageing/${caseId}?tab=capay`} className="mt-1" testId="statement-settlement-choice" />
          </div>
        )}
        <div className="rounded-md border bg-slate-50 p-3 text-sm min-h-[80px]" data-testid="statement-preview">
          {loading ? <span className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Building from LoanDisk…</span> : preview ? (
            <div className="space-y-1">
              <div className="flex flex-wrap gap-x-4 text-xs text-muted-foreground"><span>{preview.business}</span><span>{preview.full_name}</span><span>as at {preview.statement_date}</span></div>
              {preview.loans.map((l) => (
                <div key={l.sf} className="flex flex-wrap items-center gap-x-3 text-xs" data-testid={`statement-loan-${l.sf}`}>
                  <span className="font-mono font-semibold">{l.sf}</span><span>{l.status}</span><span>principal {money(cur, l.principal)}</span><span>paid {money(cur, l.paid)}</span>
                  <span className={l.balance > 0 ? "text-red-700 font-medium" : ""}>balance {money(cur, l.balance)}</span>
                  {l.restructure?.from && <span className="text-amber-700">↳ opening balance carried from {l.restructure.from.sf}</span>}
                  {l.restructure?.to && <span className="text-amber-700">→ restructured into {l.restructure.to.sf}</span>}
                </div>
              ))}
              <div className="text-sm font-semibold pt-1" data-testid="statement-total-balance">Outstanding balance: {money(cur, preview.totals?.balance)}</div>
            </div>
          ) : null}
        </div>
        {!emailMode ? (
          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="outline" onClick={download} disabled={blocked} data-testid="statement-download-btn">{busy === "pdf" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Download PDF</Button>
            <Button variant="outline" onClick={save} disabled={blocked} data-testid="statement-save-btn">{busy === "save" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FolderPlus className="w-4 h-4 mr-1" />}Save to files</Button>
            <Button onClick={() => setEmailMode(true)} disabled={blocked} data-testid="statement-email-btn"><Mail className="w-4 h-4 mr-1" />Email to customer</Button>
          </div>
        ) : (
          <div className="space-y-2 border-t pt-3" data-testid="statement-email-wizard">
            <div className="grid sm:grid-cols-2 gap-2">
              <div><Label className="text-xs">To</Label><Input value={to} onChange={(e) => setTo(e.target.value)} className="h-9" data-testid="statement-email-to" /></div>
              <div><Label className="text-xs">Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={`Your SimplyFunded statement — ${caseRow?.customer_name || ""}`} className="h-9" data-testid="statement-email-subject" /></div>
            </div>
            <div><Label className="text-xs">Message (leave blank for the standard wording)</Label><Textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} data-testid="statement-email-body" /></div>
            <p className="text-[11px] text-muted-foreground">Sent from {preview?.sender_email} with the PDF attached, filed to OneDrive → Correspondence and logged on the timeline.</p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEmailMode(false)} disabled={!!busy}>Back</Button>
              <Button onClick={send} disabled={!!busy} data-testid="statement-send-btn">{busy === "send" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}Send statement</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
